import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getCountryList, getCountryFlag } from '../utils/countryFlags';
import ProfilePicture from './ProfilePicture';
import DarkModeToggle from './DarkModeToggle';

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  profile_color: string;
  country: string | null;
  created_at: string;
}

const Profile = () => {
  const { user, logout, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetupMode = searchParams.get('setup') === 'true';
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    profile_color: '#3B82F6',
    country: '',
    email: ''
  });

  const countries = getCountryList();

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        setMessage({ type: 'error', text: 'Failed to load profile' });
        return;
      }

      setProfile(data);
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        username: data.username || '',
        profile_color: data.profile_color || '#3B82F6',
        country: data.country || '',
        email: data.email || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !profile) return;

    setSaving(true);
    setMessage(null);

    try {
      // Validate username
      if (formData.username.length < 3) {
        setMessage({ type: 'error', text: 'Username must be at least 3 characters long' });
        setSaving(false);
        return;
      }

      if (formData.username.length > 16) {
        setMessage({ type: 'error', text: 'Username must be no more than 16 characters long' });
        setSaving(false);
        return;
      }

      if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
        setMessage({ type: 'error', text: 'Username can only contain English letters, numbers, and underscores' });
        setSaving(false);
        return;
      }

      // In setup mode, require country selection and proper first name
      if (isSetupMode && !formData.country) {
        setMessage({ type: 'error', text: 'Please select your country to complete setup' });
        setSaving(false);
        return;
      }

      if (isSetupMode && formData.first_name === 'User') {
        setMessage({ type: 'error', text: 'Please enter your actual first name instead of "User"' });
        setSaving(false);
        return;
      }

      if (formData.first_name.trim().length < 1) {
        setMessage({ type: 'error', text: 'First name is required' });
        setSaving(false);
        return;
      }

      // Check if username is available (if changed)
      if (formData.username !== profile.username) {
        const { data: existingUser } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', formData.username)
          .single();

        if (existingUser) {
          setMessage({ type: 'error', text: 'Username is already taken' });
          setSaving(false);
          return;
        }
      }

      // Update profile in database using auth context (this will update the user state automatically)
      const success = await updateUserProfile({
        firstName: formData.first_name,
        lastName: formData.last_name,
        username: formData.username,
        profileColor: formData.profile_color
      });

      if (!success) {
        setMessage({ type: 'error', text: 'Failed to update profile' });
        return;
      }

      // Update country separately since it's not in the auth user object
      const { error: countryError } = await supabase
        .from('profiles')
        .update({ country: formData.country || null })
        .eq('id', user.id);

      if (countryError) {
        console.error('Error updating country:', countryError);
        setMessage({ type: 'error', text: 'Profile updated but country update failed' });
        return;
      }

      setMessage({ type: 'success', text: isSetupMode ? 'Profile setup complete! Welcome to EightyThreeLabs!' : 'Profile updated successfully!' });
      await loadProfile(); // Reload to get updated data
      
      // If in setup mode, redirect to dashboard after successful setup
      if (isSetupMode) {
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!user || !formData.email) return;

    setSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({ email: formData.email });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setMessage({ type: 'success', text: 'A confirmation email has been sent to your new email address.' });
        setIsChangingEmail(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update email' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || deleteConfirmText !== 'DELETE') return;

    try {
      // Call the database function to delete all user data
      const { error: deleteError } = await supabase.rpc('delete_user_account', {
        user_id_to_delete: user.id
      });

      if (deleteError) {
        console.error('Error deleting account:', deleteError);
        setMessage({ type: 'error', text: 'Failed to delete account' });
        return;
      }

      // Sign out and redirect
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: 'Failed to delete account' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-2xl dark:text-gray-200">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-2xl text-red-600 dark:text-red-400">Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {!isSetupMode && (
              <Link 
                to="/" 
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                ← Back to Dashboard
              </Link>
            )}
            <div className="flex items-center space-x-4">
              <DarkModeToggle />
              <button
                onClick={logout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <div className="mb-8">
          {isSetupMode ? (
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                🎉 Welcome to EightyThreeLabs!
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Let's set up your profile so you can start playing and competing
              </p>
            </div>
          ) : (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Profile Settings</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage your account and preferences</p>
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 rounded-lg p-4 ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
          }`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
                  {isSetupMode ? 'Complete Your Profile' : 'Profile Information'}
                </h3>
                
                <form onSubmit={handleSave} className="space-y-6">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    {!isChangingEmail && !isSetupMode ? (
                      <div className="flex">
                        <input
                          type="email"
                          value={profile.email}
                          disabled
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        />
                        <button
                          type="button"
                          onClick={() => setIsChangingEmail(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-r-md font-medium hover:bg-blue-700 border border-blue-600"
                        >
                          Change
                        </button>
                      </div>
                    ) : isChangingEmail && !isSetupMode ? (
                      <div className="space-y-3">
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => setIsChangingEmail(false)}
                            className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-400 dark:hover:bg-gray-500"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleUpdateEmail}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name {isSetupMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter your first name"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter your last name"
                    />
                  </div>

                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">@</span>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        placeholder="username"
                        pattern="[a-zA-Z0-9_]+"
                        minLength={3}
                        maxLength={16}
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      3-16 characters, English letters, numbers, and underscores only
                    </p>
                  </div>

                  {/* Profile Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Profile Picture Color
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">#</span>
                        <input
                          type="text"
                          value={formData.profile_color.replace('#', '')}
                          onChange={(e) => setFormData({ ...formData, profile_color: `#${e.target.value}` })}
                          className="block w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                          placeholder="3B82F6"
                          pattern="[0-9A-Fa-f]{6}"
                          maxLength={6}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Preview:</span>
                        <ProfilePicture 
                          firstName={formData.first_name}
                          profileColor={formData.profile_color || '#3B82F6'}
                          size="md"
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Enter any 6-character HTML color code (e.g., FF0000 for red)
                    </p>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Country {isSetupMode && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      required={isSetupMode}
                    >
                      <option value="">Select your country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.displayName}
                        </option>
                      ))}
                    </select>
                    {formData.country && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                        <span>Your flag:</span>
                        <span className="text-lg">{getCountryFlag(formData.country)}</span>
                      </p>
                    )}
                  </div>

                  {/* Save Button */}
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : isSetupMode ? 'Complete Setup & Start Playing!' : 'Save Changes'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Preview */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 text-center">
                <ProfilePicture 
                  firstName={formData.first_name || profile.first_name}
                  profileColor={formData.profile_color || '#3B82F6'}
                  size="xl"
                  className="mx-auto mb-4"
                />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {formData.first_name} {formData.last_name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">@{formData.username}</p>
                {formData.country && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-center space-x-1">
                    <span>{getCountryFlag(formData.country)}</span>
                    <span>{countries.find(c => c.code === formData.country)?.name}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Account Info */}
            {!isSetupMode && (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Account</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Plan</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {user?.isSubscribed ? '⭐ Premium' : '🆓 Free'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Member Since</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {new Date(profile.created_at).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                  {!user?.isSubscribed && (
                    <div className="mt-6">
                      <Link
                        to="/upgrade"
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      >
                        Upgrade to Premium
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone - Only show in normal profile mode */}
        {!isSetupMode && (
          <div className="mt-8">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border-l-4 border-red-400">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Once you delete your account, there is no going back. All your data will be permanently deleted.
                </p>
                
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Account
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type "DELETE" to confirm:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className="block w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-gray-100"
                        placeholder="DELETE"
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-400 dark:hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE'}
                        className="px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Permanently Delete Account
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;